import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { movementsApi, furnitureApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useCascadeLocation } from '../hooks/useCascadeLocation';
import {
  ArrowLeftRight,
  Plus,
  Filter,
  ChevronLeft,
  ChevronRight,
  Package,
  ArrowRight,
  ArrowLeft,
  Wrench,
  Trash2,
  X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';

const MOVEMENT_CONFIG = {
  entree: { label: 'Entrée', icon: ArrowRight, class: 'bg-emerald-100 text-emerald-700' },
  sortie: { label: 'Sortie', icon: ArrowLeft, class: 'bg-rose-100 text-rose-700' },
  transfert: { label: 'Transfert', icon: ArrowLeftRight, class: 'bg-blue-100 text-blue-700' },
  maintenance: { label: 'Maintenance', icon: Wrench, class: 'bg-orange-100 text-orange-700' },
  mise_au_rebut: { label: 'Mise au rebut', icon: Trash2, class: 'bg-rose-100 text-rose-700' }
};

const MovementsPage = () => {
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedFurniture, setSelectedFurniture] = useState(null);
  const [searchBarcode, setSearchBarcode] = useState('');
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [searchParams] = useSearchParams();
  const { isGestionnaire } = useAuth();
  const navigate = useNavigate();
  const LIMIT = 20;

  const [formData, setFormData] = useState({
    type_mouvement: 'transfert',
    commentaire: ''
  });

  const {
    sites, batiments, etages, bureaux,
    selectedSite, selectedBatiment, selectedEtage, selectedBureau,
    setSelectedSite, setSelectedBatiment, setSelectedEtage, setSelectedBureau,
    loadSites,
  } = useCascadeLocation();

  useEffect(() => {
    loadSites();
    loadMovements();

    // Check if furniture ID is in URL
    const furnitureId = searchParams.get('furniture');
    if (furnitureId) {
      loadFurnitureById(furnitureId);
      setDialogOpen(true);
    }
  }, []);

  useEffect(() => {
    setPage(0);
    loadMovements(0);
  }, [filterType]);

  const loadMovements = async (pageNum = page) => {
    setLoading(true);
    try {
      const params = { skip: pageNum * LIMIT, limit: LIMIT };
      if (filterType) params.type_mouvement = filterType;

      const response = await movementsApi.getAll(params);
      setMovements(response.data);
      setHasMore(response.data.length === LIMIT);
    } catch (error) {
      console.error('Erreur chargement mouvements:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFurnitureById = async (id) => {
    try {
      const response = await furnitureApi.getById(id);
      setSelectedFurniture(response.data);
    } catch (error) {
      console.error('Erreur chargement mobilier:', error);
    }
  };

  const searchFurniture = async () => {
    if (!searchBarcode.trim()) return;
    try {
      const response = await furnitureApi.getByBarcode(searchBarcode.trim());
      setSelectedFurniture(response.data);
      setSearchBarcode('');
    } catch (error) {
      console.error('Mobilier non trouvé');
    }
  };

  const resetForm = () => {
    setFormData({ type_mouvement: 'transfert', commentaire: '' });
    setSelectedFurniture(null);
    setSelectedSite('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFurniture) return;

    const batimentNom = batiments.find(b => b.id === selectedBatiment)?.nom || '';
    const etageNom = etages.find(e => e.id === selectedEtage)?.nom || '';
    const bureauNom = bureaux.find(b => b.id === selectedBureau)?.nom || '';

    try {
      await movementsApi.create({
        furniture_id: selectedFurniture.id,
        type_mouvement: formData.type_mouvement,
        commentaire: formData.commentaire,
        site_destination_id: selectedSite || null,
        batiment_destination: batimentNom,
        etage_destination: etageNom,
        bureau_destination: bureauNom,
      });
      setDialogOpen(false);
      resetForm();
      loadMovements();
    } catch (error) {
      console.error('Erreur création mouvement:', error);
    }
  };

  const clearFilters = () => {
    setFilterType('');
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="movements-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
            Mouvements
          </h1>
          <p className="text-slate-500 mt-1">Historique des transferts et mouvements de mobilier</p>
        </div>
        {isGestionnaire() && (
          <Button
            onClick={() => setDialogOpen(true)}
            className="bg-blue-600 hover:bg-blue-700 btn-scale"
            data-testid="new-movement-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouveau mouvement
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[180px]" data-testid="filter-type">
                <SelectValue placeholder="Type de mouvement" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(MOVEMENT_CONFIG).map(([key, config]) => (
                  <SelectItem key={key} value={key}>{config.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {filterType && (
              <Button variant="ghost" size="icon" onClick={clearFilters}>
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Movements List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : movements.length === 0 ? (
            <div className="text-center py-16">
              <ArrowLeftRight className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700">Aucun mouvement</h3>
              <p className="text-slate-500 mt-1">Les mouvements de mobilier apparaîtront ici</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {movements.map((movement) => {
                const config = MOVEMENT_CONFIG[movement.type_mouvement] || {};
                const Icon = config.icon || ArrowLeftRight;

                return (
                  <div
                    key={movement.id}
                    className="flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors"
                    data-testid={`movement-row-${movement.id}`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${config.class}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Badge className={config.class}>{config.label}</Badge>
                        <span className="font-mono text-sm text-slate-600">
                          {movement.furniture_code || 'N/A'}
                        </span>
                      </div>
                      {movement.commentaire && (
                        <p className="text-sm text-slate-500 mt-1 truncate">{movement.commentaire}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-mono text-slate-500">
                        {new Date(movement.created_at).toLocaleDateString('fr-FR')}
                      </p>
                      <p className="text-xs text-slate-400">
                        {new Date(movement.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {movements.length > 0 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const newPage = page - 1;
                  setPage(newPage);
                  loadMovements(newPage);
                }}
                disabled={page === 0}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Précédent
              </Button>
              <span className="text-sm text-slate-500">Page {page + 1}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const newPage = page + 1;
                  setPage(newPage);
                  loadMovements(newPage);
                }}
                disabled={!hasMore}
              >
                Suivant
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* New Movement Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Nouveau mouvement</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Furniture Selection */}
            {!selectedFurniture ? (
              <div className="space-y-2">
                <Label>Rechercher un mobilier</Label>
                <div className="flex gap-2">
                  <Input
                    value={searchBarcode}
                    onChange={(e) => setSearchBarcode(e.target.value)}
                    placeholder="Code-barres..."
                    className="font-mono"
                    data-testid="search-furniture-input"
                  />
                  <Button type="button" onClick={searchFurniture}>
                    Rechercher
                  </Button>
                </div>
              </div>
            ) : (
              <div className="p-4 rounded-lg bg-slate-50 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-mono font-medium">{selectedFurniture.code_barres}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedFurniture(null)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-sm text-slate-600">
                  {selectedFurniture.marque} {selectedFurniture.modele}
                </p>
                <p className="text-xs text-slate-500">
                  Site actuel: {selectedFurniture.site_nom || 'Non affecté'}
                </p>
              </div>
            )}

            {/* Movement Type */}
            <div className="space-y-2">
              <Label>Type de mouvement</Label>
              <Select
                value={formData.type_mouvement}
                onValueChange={(v) => setFormData(prev => ({ ...prev, type_mouvement: v }))}
              >
                <SelectTrigger data-testid="select-movement-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="transfert">Transfert</SelectItem>
                  <SelectItem value="sortie">Sortie de stock</SelectItem>
                  <SelectItem value="maintenance">Mise en maintenance</SelectItem>
                  <SelectItem value="mise_au_rebut">Mise au rebut</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Destination (for transfers) */}
            {formData.type_mouvement === 'transfert' && (
              <div className="space-y-3">
                {/* Site */}
                <div className="space-y-2">
                  <Label>Site de destination</Label>
                  <Select
                    value={selectedSite}
                    onValueChange={setSelectedSite}
                    data-testid="select-site-destination"
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un site" />
                    </SelectTrigger>
                    <SelectContent>
                      {sites.map(site => (
                        <SelectItem key={site.id} value={site.id}>{site.nom}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Bâtiment */}
                {selectedSite && (
                  <div className="space-y-2">
                    <Label>Bâtiment</Label>
                    <Select
                      value={selectedBatiment}
                      onValueChange={setSelectedBatiment}
                      disabled={batiments.length === 0}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={batiments.length === 0 ? 'Aucun bâtiment' : 'Sélectionner un bâtiment'} />
                      </SelectTrigger>
                      <SelectContent>
                        {batiments.map(b => (
                          <SelectItem key={b.id} value={b.id}>{b.nom}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Étage */}
                {selectedBatiment && (
                  <div className="space-y-2">
                    <Label>Étage</Label>
                    <Select
                      value={selectedEtage}
                      onValueChange={setSelectedEtage}
                      disabled={etages.length === 0}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={etages.length === 0 ? 'Aucun étage' : 'Sélectionner un étage'} />
                      </SelectTrigger>
                      <SelectContent>
                        {etages.map(e => (
                          <SelectItem key={e.id} value={e.id}>{e.nom}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Bureau */}
                {selectedEtage && (
                  <div className="space-y-2">
                    <Label>Bureau</Label>
                    <Select
                      value={selectedBureau}
                      onValueChange={setSelectedBureau}
                      disabled={bureaux.length === 0}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={bureaux.length === 0 ? 'Aucun bureau' : 'Sélectionner un bureau'} />
                      </SelectTrigger>
                      <SelectContent>
                        {bureaux.map(b => (
                          <SelectItem key={b.id} value={b.id}>{b.nom}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}

            {/* Comment */}
            <div className="space-y-2">
              <Label>Commentaire</Label>
              <Textarea
                value={formData.commentaire}
                onChange={(e) => setFormData(prev => ({ ...prev, commentaire: e.target.value }))}
                placeholder="Notes sur ce mouvement..."
                rows={3}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                Annuler
              </Button>
              <Button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700"
                disabled={!selectedFurniture}
                data-testid="submit-movement-btn"
              >
                Enregistrer
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MovementsPage;
